import { NumericFormat } from "react-number-format";
import { useFieldProperties } from "@/appComponents/Blocks/Composites/ReactInputFields/Utils";
import { forwardRef, useMemo } from "react";
import { Input } from "@theorgchartdev/storybook";
import { Globals } from "@coreapp/CoreApp/Globals";
import { QObjDBFieldDef } from "@coreapp/CoreApp/QObjDBFieldDef";
import { enumFieldType } from "@coreapp/constants";
import { GetDoc } from "@/Utils/StateLib";

const initializeDummyFieldDef = () => {
  const doc = GetDoc();
  const boxClass = doc.GetBoxClass();
  const fieldDef = new QObjDBFieldDef();
  fieldDef.SetReadOnly(true);
  fieldDef.InitField("dummy_numeric_input", "dummy_numeric_input", boxClass);
  fieldDef.SetFieldType(enumFieldType.number);
  fieldDef.SetInternalOnly(true);

  return fieldDef;
};

export const CustomNumericInput = forwardRef(({ handleValueChange, ...props }, ref) => {
  // Explicitly map `onChange` from the NumericFormat to the custom Input
  const handleDataChange = (event, value) => {
    if (handleValueChange && value === "") {
      handleValueChange({ floatValue: "" }, { event: event });
    } else {
      props?.onChange(event);
    }
  };

  return <Input {...props} ref={ref} onChange={handleDataChange} />;
});

export const NumericInputField = ({
  value,
  fieldDef,
  customInput,
  isAllowed,
  onChange,
  returnNumber,
  allowNegative,
  allowDummyFieldDef = false,
  ...customInputProps
}) => {
  if (!fieldDef) {
    // If fieldDef is not provided, create a dummy one
    if (allowDummyFieldDef === true) {
      fieldDef = initializeDummyFieldDef();
    } else {
      return;
    }
  }
  const { thousandSeparator, isNumericValue, prefix, suffix, decimalScale, showPercentage } =
    useFieldProperties({
      fieldDef
    });

  const isValidNumberStr = value => {
    if (value == null || value === "") return false; // Invalid if null or blank
    const numericValue =
      typeof value === "string" ? parseFloat(value.trim().replace(/,/g, "")) : parseFloat(value);
    return !isNaN(numericValue); // Valid if it parses to a number
  };

  const initialValue = useMemo(() => {
    if (!isValidNumberStr(value)) return undefined; // Use isValid to check for validity

    if (!isNumericValue) return value; // Return as-is if not a numeric value

    const initValue = showPercentage && value !== "" ? value * 100 : value; // Apply percentage conversion;
    return typeof initValue === "string"
      ? parseFloat(initValue.trim().replace(/,/g, ""))
      : parseFloat(initValue);
  }, [value, showPercentage]);

  const handleChange = (values, sourceInfo) => {
    // react-number-format fires onValueChange for both user input (source="event") and
    // programmatic prop changes (source="prop"). Prop-change events are re-renders, not
    // user edits — propagating them would write stale/empty values into changeResults.
    if (sourceInfo?.source === "prop") return;
    let newValue;
    if (isNumericValue) {
      newValue = values.floatValue ?? "";
      newValue = showPercentage && newValue !== "" ? newValue / 100 : newValue;
      if (returnNumber) {
        onChange?.(sourceInfo.event, newValue);
        return;
      }
    } else {
      newValue = values.floatValue?.toString() ?? "";
    }

    //console.log(`Saved value: ${newValue}, type: ${typeof newValue}`);
    const sanitized = newValue.toString().trim().replace(/,/g, "");
    onChange?.(sourceInfo.event, sanitized);
  };

  const handleAllowed = value => {
    if (isAllowed) {
      return isAllowed(value);
    }
    return true;
  };

  const inputStyle = customInputProps?.readOnly
    ? {
        border: "none",
        background: "none",
        color: "inherit",
        pointerEvents: "none",
        outline: "none"
      }
    : {};

  return (
    <NumericFormat
      value={showPercentage && value !== "" ? value * 100 : value}
      allowNegative={allowNegative ?? isNumericValue}
      customInput={CustomNumericInput}
      prefix={`${prefix} `}
      suffix={` ${showPercentage ? "%" : ""}${suffix}`}
      valueIsNumericString={isNumericValue && typeof value === "string"}
      decimalScale={decimalScale}
      decimalSeparator={Globals._decimalMarker}
      fixedDecimalScale={isNumericValue}
      thousandSeparator={thousandSeparator}
      defaultValue={initialValue}
      isAllowed={handleAllowed}
      onValueChange={handleChange}
      handleValueChange={handleChange}
      style={{ ...inputStyle, ...customInputProps?.style }}
      {...customInputProps}
    />
  );
};
